import { DESKTOP_VIEWPORT_METRICS, type ViewportMetrics } from './viewportMetrics';

export interface CanvasBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface SpawnLineCssFrame {
  left: number;
  top: number;
  width: number;
}

export function spawnLineCssFrame(
  bounds: CanvasBounds,
  viewport: ViewportMetrics = DESKTOP_VIEWPORT_METRICS,
): SpawnLineCssFrame {
  const finite = [bounds.left, bounds.top, bounds.width, bounds.height].every(Number.isFinite);
  if (!finite || bounds.width < 0 || bounds.height <= 0) return { left: 0, top: 0, width: 0 };
  return {
    left: bounds.left,
    top: bounds.top + viewport.spawnScreenY * bounds.height / viewport.stageHeight,
    width: bounds.width,
  };
}

export function projectedAabbScreenWidth(
  worldWidth: number,
  worldHeight: number,
  angleDegrees: number,
  zoom: number,
): number {
  if (![worldWidth, worldHeight, angleDegrees, zoom].every(Number.isFinite)
    || worldWidth < 0 || worldHeight < 0 || zoom <= 0) return 0;
  const radians = angleDegrees * Math.PI / 180;
  return (Math.abs(worldWidth * Math.cos(radians)) + Math.abs(worldHeight * Math.sin(radians))) * zoom;
}

export function clampAimScreenX(
  screenX: number,
  worldWidth: number,
  worldHeight: number,
  angleDegrees: number,
  zoom: number,
  logicalCanvasWidth: number,
  originX = 0.5,
  originY = 0.5,
): number {
  if (!Number.isFinite(screenX) || !Number.isFinite(logicalCanvasWidth) || logicalCanvasWidth <= 0
    || ![worldWidth, worldHeight, angleDegrees, zoom, originX, originY].every(Number.isFinite)
    || worldWidth < 0 || worldHeight < 0 || zoom <= 0) {
    return logicalCanvasWidth / 2;
  }
  const radians = angleDegrees * Math.PI / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const xs = [-originX * worldWidth, (1 - originX) * worldWidth];
  const ys = [-originY * worldHeight, (1 - originY) * worldHeight];
  const projected = xs.flatMap((cornerX) => ys.map((cornerY) => cornerX * cosine - cornerY * sine));
  const minimum = -Math.min(...projected) * zoom + 12;
  const maximum = logicalCanvasWidth - Math.max(...projected) * zoom - 12;
  if (minimum > maximum) return logicalCanvasWidth / 2;
  return Math.min(maximum, Math.max(minimum, screenX));
}
